import { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWebsiteBlock } from '@/hooks/useWebsiteBlock';
import { useUserRole } from '@/hooks/useUserRole';
import { Skeleton } from '@/components/ui/skeleton';

interface WebsiteBlockGuardProps {
  children: React.ReactNode;
}

export const WebsiteBlockGuard = ({ children }: WebsiteBlockGuardProps) => {
  const { isBlocked, isLoading: blockLoading } = useWebsiteBlock();
  const { isAdmin, isLoading: roleLoading } = useUserRole();
  const navigate = useNavigate();
  const location = useLocation();
  const isLovablePreview = typeof window !== 'undefined' && window.self !== window.top && window.location.hostname.includes('lovableproject.com');

  // In Lovable preview, redirect away from blocked page
  useEffect(() => {
    if (isLovablePreview && location.pathname === '/blocked') {
      navigate('/', { replace: true });
    }
  }, [isLovablePreview, location.pathname, navigate]);

  useEffect(() => {
    // In Lovable preview, skip blocking logic entirely
    if (isLovablePreview) return;

    // Don't redirect if still loading
    if (blockLoading || roleLoading) return;

    // Don't redirect if user is admin
    if (isAdmin) return;

    // Don't redirect if already on blocked page
    if (location.pathname === '/blocked') return;

    // Don't redirect dashboard routes or management login
    if (location.pathname.startsWith('/dashboard')) return;
    if (location.pathname === '/management') return;

    // Redirect to blocked page if website is blocked
    if (isBlocked) {
      navigate('/blocked', { replace: true });
    }
  }, [isBlocked, isAdmin, blockLoading, roleLoading, navigate, location.pathname, isLovablePreview]);

  // Show loading state while checking
  if (blockLoading || roleLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="space-y-4 w-full max-w-md p-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      </div>
    );
  }

  // If blocked and not admin, don't render children (redirect will happen)
  if (isBlocked && !isAdmin && location.pathname !== '/blocked' && !location.pathname.startsWith('/dashboard') && location.pathname !== '/management') {
    return null;
  }

  return <>{children}</>;
};
